import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    useState,
    useRef
} from "react";
import {
    type TextInputContentSizeChangeEvent,
    type TextInputEndEditingEvent,
    TextInput as NativeTextInput,
    type FocusEvent,
    type BlurEvent,
    Platform
} from "react-native";
import type ITextAreaInputProps from "./type";
import {
    type TextAreaInputInstance,
    type ITextAreaInputRef
} from "./type";
import stylesheet, {
    getIsFieldSizingSupported,
    getTextAreaInputCounter,
    getTextAreaInputType,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    type RefForwardingComponent
} from "../../types";
import {
    type FieldInputRenderPropsType,
    type FieldActionType
} from "../fieldControl/type";
import {
    getFieldSizeKey
} from "../fieldControl/stylesheet";
import {
    FieldClearIcon
} from "../fieldControl/icons";
import {
    warnOnce
} from "../../utils/development";
import {
    uuid
} from "../../utils";
import FieldControl from "../fieldControl";

const isAutoGrowFallback = Platform.OS === "web" && !getIsFieldSizingSupported();

const TextAreaInput: RefForwardingComponent<ITextAreaInputRef, ITextAreaInputProps> = ({
    onContentSizeChange: onContentSizeChangeProp,
    rightIcon: RightIconComponentProp,
    onChangeText: onChangeTextProp,
    onEndEditing: onEndEditingProp,
    spreadBehaviour = "baseline",
    isAutoKeyboardDismissOnBlur,
    rightIconAccessibilityLabel,
    isShowHintTextIcon = false,
    isShowLengthLimiter = true,
    isUpdateOnRealtime = false,
    icon: IconComponentProp,
    hintTextContainerStyle,
    iconAccessibilityLabel,
    contentContainerStyle,
    onFocus: onFocusProp,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    onBlur: onBlurProp,
    customBorderColor,
    isLoading = false,
    customTitleColor,
    rightIconOnPress,
    type = "neutral",
    value: valueProp,
    customIconColor,
    maxHeight = 350,
    cleanIconStyle,
    clearIconStyle,
    customLocalize,
    isCleanEnabled,
    isClearEnabled,
    isShowSubTitle,
    rightIconStyle,
    defaultValue,
    hintTextIcon,
    initialValue,
    customColor,
    customTheme,
    iconOnPress,
    inputStyle,
    isOptional,
    validation,
    iconStyle,
    maxLength,
    minHeight,
    editable,
    hintText,
    readOnly,
    subTitle,
    style,
    title,
    size,
    id,
    ...props
}, ref) => {
    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const inputRef = useRef<TextAreaInputInstance | null>(null);

    const committedValue = useRef(initialValue ?? defaultValue ?? "");

    const generatedID = useRef(uuid());

    const [
        value,
        setValue
    ] = useState(initialValue ?? defaultValue ?? "");

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const [
        contentHeight,
        setContentHeight
    ] = useState<number | undefined>(undefined);

    const currentType = getTextAreaInputType({
        type
    });

    const isCurrentReadOnly = isReadOnly || readOnly === true || editable === false;

    const isCurrentClearEnabled = isClearEnabled ?? isCleanEnabled ?? false;

    const isClearActionAvailable = isCurrentClearEnabled && !isDisabled && !isCurrentReadOnly;

    const isCleanIconStyleUsed = cleanIconStyle !== undefined;

    const isRightIconPressable = !!rightIconOnPress;

    const isIconPressable = !!iconOnPress;

    const currentCounter = isShowLengthLimiter && maxLength ? getTextAreaInputCounter({
        length: value.length,
        locale: activeLocale,
        maxLength
    }) : undefined;

    const currentSize = getFieldSizeKey({
        isActionable: isCurrentClearEnabled || isIconPressable || isRightIconPressable,
        pointerType,
        size
    });

    const {
        input: inputDynamicStyle
    } = useStyles({
        contentHeight: isAutoGrowFallback ? contentHeight : undefined
    });

    useImperativeHandle(
        ref,
        () => ({
            updateValue,
            cleanText,
            clearText,
            focus,
            blur
        }),
        [
            onChangeTextProp
        ]
    );

    useEffect(() => {
        if(valueProp === undefined) {
            return;
        }

        committedValue.current = valueProp;

        setValue(valueProp);
    }, [
        valueProp
    ]);

    useEffect(() => {
        if(type !== "default") {
            return;
        }

        warnOnce({
            message: "TextAreaInput: type \"default\" is deprecated and will be removed in the next major version. Use \"neutral\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(type !== "question") {
            return;
        }

        warnOnce({
            message: "TextAreaInput: type \"question\" is deprecated and will be removed in the next major version. Use type \"neutral\" with \"isShowHintTextIcon\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(isCleanEnabled === undefined) {
            return;
        }

        warnOnce({
            message: "TextAreaInput: \"isCleanEnabled\" is deprecated and will be removed in the next major version. Use \"isClearEnabled\" instead."
        });
    }, [
        isCleanEnabled
    ]);

    useEffect(() => {
        if(!isCleanIconStyleUsed) {
            return;
        }

        warnOnce({
            message: "TextAreaInput: \"cleanIconStyle\" is deprecated and will be removed in the next major version. Use \"clearIconStyle\" instead."
        });
    }, [
        isCleanIconStyleUsed
    ]);

    useEffect(() => {
        if(isAutoKeyboardDismissOnBlur === undefined) {
            return;
        }

        warnOnce({
            message: "TextAreaInput: \"isAutoKeyboardDismissOnBlur\" is deprecated and has no effect. A field that loses focus already closes the keyboard unless focus moves to another field."
        });
    }, [
        isAutoKeyboardDismissOnBlur
    ]);

    useEffect(() => {
        if(!isIconPressable || iconAccessibilityLabel) {
            return;
        }

        warnOnce({
            message: "TextAreaInput: a pressable \"icon\" (iconOnPress) needs an \"iconAccessibilityLabel\" so screen readers can announce it."
        });
    }, [
        iconAccessibilityLabel,
        isIconPressable
    ]);

    useEffect(() => {
        if(!isRightIconPressable || rightIconAccessibilityLabel) {
            return;
        }

        warnOnce({
            message: "TextAreaInput: a pressable \"rightIcon\" (rightIconOnPress) needs a \"rightIconAccessibilityLabel\" so screen readers can announce it."
        });
    }, [
        rightIconAccessibilityLabel,
        isRightIconPressable
    ]);

    const getIsValid = (text: string) => {
        return text === "" || !validation || validation(text);
    };

    const commitValue = (text: string) => {
        committedValue.current = text;

        if(onChangeTextProp) {
            onChangeTextProp(text);
        }
    };

    const finishEditing = (text: string) => {
        if(text === committedValue.current) {
            return;
        }

        if(getIsValid(text)) {
            commitValue(text);

            return;
        }

        setValue(committedValue.current);
    };

    const focus = () => {
        inputRef.current?.focus();
    };

    const blur = () => {
        inputRef.current?.blur();
    };

    const clearText = () => {
        setValue("");

        setContentHeight(undefined);

        commitValue("");
    };

    const cleanText = () => {
        warnOnce({
            message: "TextAreaInput: ref method \"cleanText\" is deprecated and will be removed in the next major version. Use \"clearText\" instead."
        });

        clearText();
    };

    const updateValue = (text: string) => {
        committedValue.current = text;

        setValue(text);
    };

    const onClearPress = () => {
        clearText();

        focus();
    };

    const onChangeText = (text: string) => {
        const isValid = getIsValid(text);

        if(isUpdateOnRealtime && !isValid) {
            return;
        }

        setValue(text);

        if(text === "") {
            setContentHeight(undefined);
        }

        if(isUpdateOnRealtime || Platform.OS === "web" && isValid) {
            commitValue(text);
        }
    };

    const onEndEditing = (event: TextInputEndEditingEvent) => {
        if(!isUpdateOnRealtime) {
            finishEditing(event.nativeEvent.text);
        }

        if(onEndEditingProp) {
            onEndEditingProp(event);
        }
    };

    const onContentSizeChange = (event: TextInputContentSizeChangeEvent) => {
        setContentHeight(event.nativeEvent.contentSize.height);

        if(onContentSizeChangeProp) {
            onContentSizeChangeProp(event);
        }
    };

    const onFocus = (event: FocusEvent) => {
        setIsFocused(true);

        if(onFocusProp) {
            onFocusProp(event);
        }
    };

    const onBlur = (event: BlurEvent) => {
        setIsFocused(false);

        if(Platform.OS === "web" && !isUpdateOnRealtime) {
            finishEditing(value);
        }

        if(onBlurProp) {
            onBlurProp(event);
        }
    };

    const getSubTitle = () => {
        if(isOptional) {
            return isRequired ? undefined : localize("is-optional");
        }

        if(!isShowSubTitle) {
            return undefined;
        }

        return subTitle ?? localize("is-optional");
    };

    const getStartAction = (): FieldActionType | undefined => {
        if(!IconComponentProp) {
            return undefined;
        }

        return {
            accessibilityLabel: iconAccessibilityLabel,
            icon: IconComponentProp,
            onPress: iconOnPress,
            style: iconStyle,
            customIconColor
        };
    };

    const getEndAction = (): FieldActionType | undefined => {
        if(isClearActionAvailable && (value.length > 0 || !RightIconComponentProp)) {
            return {
                style: clearIconStyle ?? cleanIconStyle,
                accessibilityLabel: localize("clear"),
                isVisible: value.length > 0,
                isControllingInput: true,
                onPress: onClearPress,
                icon: FieldClearIcon
            };
        }

        if(!RightIconComponentProp) {
            return undefined;
        }

        return {
            accessibilityLabel: rightIconAccessibilityLabel,
            icon: RightIconComponentProp,
            onPress: rightIconOnPress,
            style: rightIconStyle
        };
    };

    const renderInput = ({
        style: inputFieldStyle,
        placeholderTextColor,
        accessibilityProps
    }: FieldInputRenderPropsType) => {
        return <NativeTextInput
            {...accessibilityProps}
            {...props}
            onContentSizeChange={isAutoGrowFallback ? onContentSizeChange : onContentSizeChangeProp}
            editable={!isDisabled && !isCurrentReadOnly}
            placeholderTextColor={placeholderTextColor}
            underlineColorAndroid="transparent"
            onChangeText={onChangeText}
            onEndEditing={onEndEditing}
            maxLength={maxLength}
            style={[
                inputStyle,
                stylesheet.input,
                inputFieldStyle,
                inputDynamicStyle
            ]}
            onFocus={onFocus}
            multiline={true}
            onBlur={onBlur}
            ref={inputRef}
            value={value}
        />;
    };

    return <FieldControl
        isShowHintTextIcon={isShowHintTextIcon || type === "question"}
        type={currentCounter?.isOver ? "danger" : currentType}
        hintTextContainerStyle={hintTextContainerStyle}
        isCounterWarning={currentCounter?.isWarning}
        isCounterOver={currentCounter?.isFull}
        isCounterLive={currentCounter?.isLive}
        customBorderColor={customBorderColor}
        controlStyle={contentContainerStyle}
        inputID={id ?? generatedID.current}
        customTitleColor={customTitleColor}
        counterText={currentCounter?.text}
        spreadBehaviour={spreadBehaviour}
        customLocalize={customLocalize}
        startAction={getStartAction()}
        isReadOnly={isCurrentReadOnly}
        hintTextIcon={hintTextIcon}
        endAction={getEndAction()}
        renderInput={renderInput}
        customColor={customColor}
        customTheme={customTheme}
        subTitle={getSubTitle()}
        isDisabled={isDisabled}
        isRequired={isRequired}
        onFocusRequest={focus}
        maxHeight={maxHeight}
        minHeight={minHeight}
        isFocused={isFocused}
        isLoading={isLoading}
        hintText={hintText}
        isMultiline={true}
        size={currentSize}
        title={title}
        style={style}
    />;
};
export default forwardRef(TextAreaInput);
